import type { ComponentType, SVGProps } from "react";
import { useLocation, useParams } from "react-router";

import {
  ChatBubbleBottomCenterIcon,
  Cog6ToothIcon,
  CreditCardIcon,
  DocumentChartBarIcon,
  DocumentTextIcon,
  FolderIcon,
  HomeIcon,
  MegaphoneIcon,
  PencilSquareIcon,
  QuestionMarkCircleIcon,
  ReceiptPercentIcon,
  UserGroupIcon,
  WrenchIcon,
} from "@heroicons/react/24/outline";

import Link from "~/atoms/Link";
import AccountSetupButton from "~/components/AccountSetupButton";
import CreateNewButton from "~/components/CreateNewButton";
import useAuth from "~/hooks/auth/useAuth";
import useActionRequiredDocumentCount from "~/hooks/documents/useActionRequiredDocumentCount";
import useGetAllInvitations from "~/hooks/invitations/useGetAllInvitations";
import useFeatureFlags from "~/hooks/useFeatureFlags";
import classNames from "~/util/classNames";

declare global {
  interface Window {
    FreshworksWidget?: (action: string, type?: string) => void;
  }
}

interface HelpLinkProps {
  closeMenu?: () => void;
}

const FeedbackButton = ({ closeMenu }: HelpLinkProps) => {
  return (
    <li>
      <a
        className="group flex items-center justify-between rounded-md p-2 text-sm leading-6 font-semibold text-gray-600 duration-200 ease-in-out hover:bg-gray-50 hover:text-blue-800 [@media(min-height:700px)_and_(max-height:830px)_and_(min-width:1000px)]:py-1.5"
        href="https://airtable.com/appkaF1vgIhxZrKJ8/shrwh1wDazGrC1Fgt"
        target="_blank"
        onClick={() => {
          if (closeMenu) closeMenu();
        }}
      >
        <span className="flex items-center gap-x-3">
          <ChatBubbleBottomCenterIcon className="h-6 w-6" />
          Submit Feedback
        </span>
      </a>
    </li>
  );
};

const HelpLink = ({ closeMenu }: HelpLinkProps) => {
  const handleOnClick = () => {
    if (window && window?.FreshworksWidget) {
      window.FreshworksWidget("open");
    }

    if (closeMenu) closeMenu();
  };

  return (
    <li>
      <button
        onClick={handleOnClick}
        className="group flex w-full items-center justify-between rounded-md p-2 text-sm leading-6 font-semibold text-gray-600 duration-200 ease-in-out hover:bg-gray-50 hover:text-blue-800 [@media(min-height:700px)_and_(max-height:830px)_and_(min-width:1000px)]:py-1.5"
      >
        <span className="flex items-center gap-x-3">
          <QuestionMarkCircleIcon className="h-6 w-6" aria-hidden="true" />
          Help
        </span>
      </button>
    </li>
  );
};

interface NavigationItem {
  name: string;
  href?: string;
  subHref?: string;
  icon?: ComponentType<SVGProps<SVGSVGElement>>;
  type?: "break";
}

const landlordNavigation = ({ isSigningEnabled }): NavigationItem[] => {
  return [
    { name: "General", type: "break" },
    {
      name: "Properties",
      href: "/landlord",
      subHref: "properties",
      icon: HomeIcon,
    },
    { name: "Leases", href: "/landlord/leases", icon: PencilSquareIcon },
    { name: "People", href: "/landlord/people", icon: UserGroupIcon },
    {
      name: "Marketing",
      href: "/landlord/marketing",
      icon: MegaphoneIcon,
    },
    { name: "Maintenance", type: "break" },
    { name: "Maintenance", href: "/landlord/maintenance", icon: WrenchIcon },
    { name: "Documents", type: "break" },
    {
      name: `Docs${isSigningEnabled ? " & E-Sign" : ""}`,
      href: "/landlord/documents",
      icon: DocumentTextIcon,
    },
    { name: "Financial", type: "break" },
    {
      name: "Transactions",
      href: "/landlord/transactions",
      icon: CreditCardIcon,
    },
    { name: "Accounts", href: "/landlord/accounts", icon: FolderIcon },
    { name: "Expenses", href: "/landlord/expenses", icon: ReceiptPercentIcon },
    { name: "Reports", href: "/landlord/reports", icon: DocumentChartBarIcon },
  ];
};

const tenantNavigation = ({ isSigningEnabled }): NavigationItem[] => [
  {
    name: "Invitations",
    href: "/tenant/invitations",
  },
  { name: "General", type: "break" },
  { name: "Leases", href: "/tenant/leases", icon: PencilSquareIcon },
  {
    name: `Docs${isSigningEnabled ? " & E-Sign" : ""}`,
    href: "/tenant/documents",
    icon: DocumentTextIcon,
  },
  { name: "Maintenance", href: "/tenant/maintenance", icon: WrenchIcon },
  { name: "Financial", type: "break" },
  {
    name: "Payment Methods",
    href: "/tenant/payment-methods",
    icon: CreditCardIcon,
  },
];

interface SettingsProps {
  role: string;
}

const Settings = ({ role }: SettingsProps) => (
  <li className="mt-auto">
    <Link
      href={role === "landlord" ? "/settings" : "/settings/user-settings"}
      className="group -mx-2 flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold text-gray-700 duration-200 ease-in-out hover:bg-gray-50 hover:text-blue-800"
    >
      <Cog6ToothIcon
        className="h-6 w-6 shrink-0 text-gray-400 duration-200 ease-in-out group-hover:text-blue-800"
        aria-hidden="true"
      />
      Settings
    </Link>
  </li>
);

interface MenuListItemProps {
  label: string;
  href: string;
  isActive: boolean;
  icon?: ComponentType<SVGProps<SVGSVGElement>>;
  note?: string | number;
  closeMenu?: () => void;
}

const MenuListItem = ({
  label,
  href,
  isActive,
  icon: Icon,
  note,
  closeMenu,
}: MenuListItemProps) => {
  return (
    <li key={label}>
      <Link
        href={href}
        className={classNames(
          isActive
            ? "bg-blue-50 text-blue-700"
            : "text-gray-600 hover:bg-gray-50 hover:text-blue-800",
          "group flex items-center justify-between rounded-md p-2 text-sm leading-6 font-semibold duration-200 ease-in-out [@media(min-height:700px)_and_(max-height:830px)_and_(min-width:1000px)]:py-1.5"
        )}
        onClick={() => {
          if (closeMenu && isActive) closeMenu();
        }}
      >
        <span className="flex gap-x-3">
          {Icon && (
            <Icon
              className={classNames(
                isActive
                  ? "text-blue-700"
                  : "text-gray-500 group-hover:text-blue-800",
                "h-6 w-6 shrink-0 duration-200 ease-in-out"
              )}
              aria-hidden="true"
            />
          )}
          {label}
        </span>

        {note && (
          <span className="text-sm font-medium text-red-600">{note}</span>
        )}
      </Link>
    </li>
  );
};

interface TenantInvitationItemProps {
  isActive: boolean;
}

const TenantInvitationItem = ({ isActive }: TenantInvitationItemProps) => {
  const { data: invitationData, isLoading: invitationsLoading } =
    useGetAllInvitations({
      depth: 1,
      status: "pending",
    });

  const unacceptedInvitations = invitationsLoading
    ? []
    : (invitationData?.filter(
        (lease: { status: string }) => lease.status !== "accepted"
      ) ?? []);

  const hasPendingInvites = unacceptedInvitations.length > 0;

  if (!hasPendingInvites) return null;

  const note = hasPendingInvites ? unacceptedInvitations?.length : null;

  return (
    <li>
      <Link
        href="/tenant/invitations"
        className={classNames(
          isActive
            ? "bg-red-50 text-red-700"
            : "bg-red-50 text-red-600 hover:text-red-800",
          "group flex items-center justify-between rounded-md p-2 text-sm leading-6 font-semibold [@media(min-height:700px)_and_(max-height:830px)_and_(min-width:1000px)]:py-1.5"
        )}
      >
        <span className="flex gap-x-3">Invitations</span>

        {note && (
          <span className="text-sm font-medium text-red-600">{note}</span>
        )}
      </Link>
    </li>
  );
};

interface DocumentsItemProps {
  href: string;
  label: string;
  isActive: boolean;
  closeMenu?: () => void;
}

const DocumentsItem = ({
  href,
  label,
  isActive,
  closeMenu,
}: DocumentsItemProps) => {
  const { count } = useActionRequiredDocumentCount();

  return (
    <MenuListItem
      label={label}
      href={href}
      isActive={isActive}
      icon={DocumentTextIcon}
      note={count > 0 ? count : undefined}
      closeMenu={closeMenu}
    />
  );
};

interface MenuListProps {
  closeMenu?: () => void;
}

const MenuList = ({ closeMenu }: MenuListProps) => {
  const { user, isLoading: isUserLoading } = useAuth();
  const { data: featureFlags, isLoading: featureFlagsLoading } =
    useFeatureFlags();

  const isLoading = isUserLoading || featureFlagsLoading;

  const isSupportEnabled = featureFlags?.support;
  const isFeedbackEnabled = featureFlags?.feedback;
  const isSigningEnabled = featureFlags?.signing;

  const role = user?.internalRole || null;

  const pathname = useLocation().pathname;
  const { leaseId } = useParams();

  let mainPath =
    pathname.split("/").length > 2
      ? pathname.split("/").slice(0, 3).join("/")
      : pathname.split("/").join("/");

  mainPath = mainPath.replace(/\/+$/, "");

  if (isLoading) return null;

  // Handle which menu items to show based on the user's role and section
  let menuItems: NavigationItem[] = [];
  if (role === "landlord") {
    menuItems = landlordNavigation({ isSigningEnabled });
  }

  if (role === "tenant") {
    menuItems = tenantNavigation({ isSigningEnabled });
  }

  return (
    <nav className="flex flex-1 flex-col">
      <ul
        role="list"
        className="flex flex-1 flex-col gap-y-7 [@media(min-height:700px)_and_(max-height:830px)_and_(min-width:1000px)]:gap-y-1"
      >
        <li>
          <ul role="list" className="-mx-3 space-y-1">
            {role === "landlord" && (
              <>
                <AccountSetupButton position="top" />
                <CreateNewButton />
              </>
            )}
            {menuItems.filter(Boolean).map((item) => {
              if (item.type === "break") {
                return (
                  <li key={item.name}>
                    <span className="my-3 block text-xs font-medium text-gray-400 uppercase [@media(min-height:700px)_and_(max-height:830px)_and_(min-width:1000px)]:mb-1">
                      {item.name}
                    </span>
                  </li>
                );
              }

              const menuLink =
                item.href?.replace("[id]", leaseId as string) || "";

              const isActive =
                pathname === item?.href ||
                mainPath === item?.href ||
                (item.subHref && pathname.includes(item.subHref)) ||
                menuLink === pathname;

              if (item.href === "/tenant/invitations") {
                return (
                  <TenantInvitationItem key={item.href} isActive={isActive} />
                );
              }

              if (
                item.href === "/tenant/documents" ||
                item.href === "/landlord/documents"
              ) {
                return (
                  <DocumentsItem
                    key={item.href}
                    href={item.href}
                    label={item.name}
                    isActive={isActive}
                    closeMenu={closeMenu}
                  />
                );
              }

              return (
                <MenuListItem
                  key={item.href}
                  label={item.name}
                  href={menuLink}
                  isActive={isActive}
                  icon={item.icon}
                  closeMenu={closeMenu}
                />
              );
            })}
            {isSupportEnabled || isFeedbackEnabled ? (
              <li>
                <span className="my-3 block text-xs font-medium text-gray-400 uppercase [@media(min-height:700px)_and_(max-height:830px)_and_(min-width:1000px)]:mb-1">
                  Support
                </span>
              </li>
            ) : null}
            {isSupportEnabled && <HelpLink closeMenu={closeMenu} />}
            {isFeedbackEnabled && <FeedbackButton closeMenu={closeMenu} />}
            {role === "landlord" && <AccountSetupButton position="bottom" />}
          </ul>
        </li>
        <Settings role={role as string} />
      </ul>
    </nav>
  );
};

export default MenuList;
